import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useAppDispatch } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { CustomWorkflowSummary, useDisableCustomWorkflowMutation, useEnableCustomWorkflowMutation } from '../../../store/customWorkflow/customWorkflows'

interface EnableWorkflowModalProps {
  accountId: string
  workflow: CustomWorkflowSummary
  enablementId: string | null
  show: boolean
  onHide: () => void
}

export const EnableWorkflowModal: React.FC<EnableWorkflowModalProps> = ({ accountId, workflow, enablementId, show, onHide }) => {
  const dispatch = useAppDispatch()
  const [enableCustomWorkflow, { isLoading: enabling }] = useEnableCustomWorkflowMutation()
  const [disableCustomWorkflow, { isLoading: disabling }] = useDisableCustomWorkflowMutation()
  const [override, setOverride] = useState<boolean | null>(null)

  const serverEnabled = enablementId !== null
  if (override !== null && override === serverEnabled) {
    setOverride(null)
  }
  const checked = override ?? serverEnabled
  const busy = enabling || disabling

  const handleToggle = async (next: boolean) => {
    setOverride(next)
    try {
      if (next) {
        await enableCustomWorkflow({ accountId, customWorkflowId: workflow.id }).unwrap()
      } else if (enablementId) {
        await disableCustomWorkflow({ accountId, enabledCustomWorkflowId: enablementId }).unwrap()
      }
    } catch {
      setOverride(!next)
      dispatch(addToast({ variant: 'error', title: 'Could not update workflow', messages: ['Please try again.'] }))
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      bodyClassName='pb-4'
      header={<h5 className='mb-0'>{workflow.name || workflow.id}</h5>}
      body={
        <div className='d-flex flex-column zocks-gap-2'>
          {workflow.description ? <div className='paragraph-medium zocks-text-gray-700'>{workflow.description}</div> : null}
          {workflow.tags.length ? (
            <div className='d-flex gap-1 flex-wrap'>
              {workflow.tags.map((tag) => (
                <span key={tag} className='px-2 py-1 border zocks-border-color-gray-300 rounded paragraph-small zocks-text-gray-600'>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
          <div className='d-flex justify-content-between align-items-center border-top pt-3'>
            <div>
              <div className='fw-semibold'>Enable this workflow</div>
              <div className='paragraph-small zocks-text-gray-600'>When enabled, it runs automatically whenever its trigger event fires.</div>
            </div>
            <ScopedBlock scope={{ scope: 'custom_workflow:create', superScope: ['custom_workflow:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
              <Form.Switch role='switch' aria-label='Enable this workflow' checked={checked} disabled={busy} onChange={(e) => handleToggle(e.target.checked)} />
            </ScopedBlock>
          </div>
        </div>
      }
    />
  )
}
